import {
  type Instant,
  type LocalDate,
  type Money,
  formatLocalDate,
  fromApiMoney,
  parseInstant,
  parseLocalDate,
  toApiMoney,
} from '@taler/domain'
import type { Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, type Invalidations, ledgerKey } from '../http/keys.ts'

export type ImportJobStatus = Schemas['ImportJobResponse']['status']

export interface ImportJob {
  readonly id: string
  readonly accountId: string
  readonly filename: string
  readonly status: ImportJobStatus
  readonly createdAt: Instant
  readonly committedAt: Instant | undefined
  readonly abandonedAt: Instant | undefined
}

export interface ImportRawRow {
  readonly id: string
  readonly rowNumber: number
  readonly columns: Readonly<Record<string, string>>
  /** Set instead of a preview entry existing for this row - see `ImportRowMapper` (taler-backend).
   * There is no reprocess action: a corrected file is a new job. */
  readonly parseError: string | undefined
}

/** A proposal, not a posting - becomes real only through `commit`. [counterAccountId] is
 * `undefined` until review sets one; commit refuses a non-`excluded` row that still has none.
 * [duplicateOfEntryId], when set, names an already-*posted* journal entry (any source) sharing
 * this row's date, amount and both accounts - a hint only, never auto-excluded. */
export interface ImportPreviewEntry {
  readonly id: string
  readonly rawRowId: string
  readonly entryDate: LocalDate
  readonly counterAccountId: string | undefined
  readonly amount: Money
  readonly description: string
  readonly externalReference: string | undefined
  readonly excluded: boolean
  readonly duplicateOfEntryId: string | undefined
  readonly postedEntryId: string | undefined
  readonly createdAt: Instant
  readonly updatedAt: Instant
}

/** Which of the file's own headers holds each canonical field - the file never dictates field
 * names. A header this mapping names that the file doesn't have reads as blank for every row,
 * which becomes a per-row parse failure rather than an upfront rejection. */
export interface ImportColumnMapping {
  readonly date: string
  readonly amount: string
  readonly description: string
  readonly accountCode: string | undefined
  readonly externalReference: string | undefined
}

export interface CreateImportJobInput {
  readonly accountId: string
  readonly file: File
  readonly mapping: ImportColumnMapping
}

export interface EditImportPreviewEntryInput {
  readonly entryDate: LocalDate
  readonly counterAccountId: string | undefined
  readonly amount: Money
  readonly description: string
  readonly excluded: boolean
}

export const importKeys = {
  list: (ledgerId: string): CacheKey => ledgerKey(ledgerId, 'imports', 'list'),
  detail: (ledgerId: string, jobId: string): CacheKey => ledgerKey(ledgerId, 'imports', 'detail', jobId),
  rawRows: (ledgerId: string, jobId: string): CacheKey =>
    ledgerKey(ledgerId, 'imports', 'detail', jobId, 'raw-rows'),
  previewEntries: (ledgerId: string, jobId: string): CacheKey =>
    ledgerKey(ledgerId, 'imports', 'detail', jobId, 'preview-entries'),
  /** Committing posts journal entries, so the journal and any reporting read must be treated
   * as stale too - the same reasoning as `receiptScanKeys.invalidatedByWrite`. */
  invalidatedByWrite: (ledgerId: string): Invalidations => [
    ledgerKey(ledgerId, 'imports'),
    ledgerKey(ledgerId, 'entries'),
    ledgerKey(ledgerId, 'reporting'),
  ],
} as const

export interface ImportsRepository {
  list(ledgerId: string, signal?: AbortSignal): Promise<readonly ImportJob[]>
  get(ledgerId: string, jobId: string, signal?: AbortSignal): Promise<ImportJob>
  rawRows(ledgerId: string, jobId: string, signal?: AbortSignal): Promise<readonly ImportRawRow[]>
  previewEntries(
    ledgerId: string,
    jobId: string,
    signal?: AbortSignal,
  ): Promise<readonly ImportPreviewEntry[]>
  create(ledgerId: string, input: CreateImportJobInput): Promise<ImportJob>
  editPreviewEntry(
    ledgerId: string,
    entryId: string,
    input: EditImportPreviewEntryInput,
  ): Promise<ImportPreviewEntry>
  commit(ledgerId: string, jobId: string): Promise<ImportJob>
  abandon(ledgerId: string, jobId: string): Promise<ImportJob>
}

export function createImportsRepository(client: TalerClient): ImportsRepository {
  const base = (ledgerId: string) => `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/import-jobs`
  const one = (ledgerId: string, jobId: string) => `${base(ledgerId)}/${encodeURIComponent(jobId)}`
  const previewEntry = (ledgerId: string, entryId: string) =>
    `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/import-preview-entries/${encodeURIComponent(entryId)}`

  return {
    async list(ledgerId, signal) {
      const jobs = await client.call<Schemas['ImportJobResponse'][]>({
        method: 'GET',
        path: base(ledgerId),
        ...(signal === undefined ? {} : { signal }),
      })
      return jobs.map(toImportJob)
    },

    async get(ledgerId, jobId, signal) {
      return toImportJob(
        await client.call<Schemas['ImportJobResponse']>({
          method: 'GET',
          path: one(ledgerId, jobId),
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },

    async rawRows(ledgerId, jobId, signal) {
      const rows = await client.call<Schemas['ImportRawRowResponse'][]>({
        method: 'GET',
        path: `${one(ledgerId, jobId)}/raw-rows`,
        ...(signal === undefined ? {} : { signal }),
      })
      return rows.map(toImportRawRow)
    },

    async previewEntries(ledgerId, jobId, signal) {
      const entries = await client.call<Schemas['ImportPreviewEntryResponse'][]>({
        method: 'GET',
        path: `${one(ledgerId, jobId)}/preview-entries`,
        ...(signal === undefined ? {} : { signal }),
      })
      return entries.map(toImportPreviewEntry)
    },

    async create(ledgerId, input) {
      const formData = new FormData()
      formData.append('file', input.file, input.file.name)
      formData.append(
        'mapping',
        new Blob([JSON.stringify(mappingRequest(input.mapping))], { type: 'application/json' }),
      )
      return toImportJob(
        await client.callMultipart<Schemas['ImportJobResponse']>({
          method: 'POST',
          path: base(ledgerId),
          query: { accountId: input.accountId },
          formData,
          idempotencyPayload: {
            accountId: input.accountId,
            mapping: input.mapping,
            filename: input.file.name,
            size: input.file.size,
            lastModified: input.file.lastModified,
          },
        }),
      )
    },

    async editPreviewEntry(ledgerId, entryId, input) {
      const path = previewEntry(ledgerId, entryId)
      const body: Schemas['EditPreviewEntryRequest'] = {
        entryDate: formatLocalDate(input.entryDate),
        ...(input.counterAccountId === undefined ? {} : { counterAccountId: input.counterAccountId }),
        amount: toApiMoney(input.amount).amount,
        description: input.description,
        excluded: input.excluded,
      }
      const response = await client.call<
        Schemas['ImportPreviewEntryResponse'],
        Schemas['EditPreviewEntryRequest']
      >({
        method: 'PATCH',
        path,
        body,
      })
      // `excluded` is a toggle: a reviewer can legitimately send this exact body again later
      // (re-excluding a row they had un-excluded), and a content-addressed idempotency key
      // would otherwise replay this call's own response forever. Forgetting it here, right
      // after a successful send, is what `IdempotencyKeys.forget` exists for.
      client.idempotencyKeys.forget({ method: 'PATCH', path, query: null, body })
      return toImportPreviewEntry(response)
    },

    async commit(ledgerId, jobId) {
      return toImportJob(
        await client.call<Schemas['ImportJobResponse'], Record<string, never>>({
          method: 'POST',
          path: `${one(ledgerId, jobId)}/commit`,
          body: {},
        }),
      )
    },

    async abandon(ledgerId, jobId) {
      return toImportJob(
        await client.call<Schemas['ImportJobResponse'], Record<string, never>>({
          method: 'POST',
          path: `${one(ledgerId, jobId)}/abandon`,
          body: {},
        }),
      )
    },
  }
}

function mappingRequest(mapping: ImportColumnMapping): Schemas['ImportColumnMappingRequest'] {
  return {
    date: mapping.date,
    amount: mapping.amount,
    description: mapping.description,
    ...(mapping.accountCode === undefined ? {} : { accountCode: mapping.accountCode }),
    ...(mapping.externalReference === undefined ? {} : { externalReference: mapping.externalReference }),
  }
}

function toImportJob(response: Schemas['ImportJobResponse']): ImportJob {
  return {
    id: response.id,
    accountId: response.accountId,
    filename: response.filename,
    status: response.status,
    createdAt: parseInstant(response.createdAt),
    committedAt: response.committedAt === undefined ? undefined : parseInstant(response.committedAt),
    abandonedAt: response.abandonedAt === undefined ? undefined : parseInstant(response.abandonedAt),
  }
}

function toImportRawRow(response: Schemas['ImportRawRowResponse']): ImportRawRow {
  return {
    id: response.id,
    rowNumber: response.rowNumber,
    columns: response.columns ?? {},
    parseError: response.parseError,
  }
}

function toImportPreviewEntry(response: Schemas['ImportPreviewEntryResponse']): ImportPreviewEntry {
  return {
    id: response.id,
    rawRowId: response.rawRowId,
    entryDate: parseLocalDate(response.entryDate),
    counterAccountId: response.counterAccountId,
    amount: fromApiMoney(response.amount),
    description: response.description,
    externalReference: response.externalReference,
    excluded: response.excluded,
    duplicateOfEntryId: response.duplicateOfEntryId,
    postedEntryId: response.postedEntryId,
    createdAt: parseInstant(response.createdAt),
    updatedAt: parseInstant(response.updatedAt),
  }
}
